import { html, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ContextConsumer } from '@lit-labs/context';
import { classMap } from 'lit/directives/class-map.js';

import { Component } from '../component';
import styles from './text-field.styles';
import fieldContext from '../field/field.context';
import '../layout/group';
import '../close-button';

import type { FieldStatus } from '../field';

export type TextFieldSize = 'small' | 'default' | 'large';

export interface TextFieldProps {
	clearable?: boolean;
	disabled?: boolean;
	max?: string;
	'max-length'?: number;
	min?: string;
	'min-length'?: number;
	pattern?: string;
	placeholder?: string;
	'read-only'?: boolean;
	size?: TextFieldSize;
	status?: FieldStatus;
	step?: string;
	type?: 'text' | 'password' | 'email' | 'number' | 'search' | 'tel' | 'url';
	value?: string;
	'm-clear'?: (event: CustomEvent) => void;
	change?: (event: Event) => void;
	input?: (event: Event) => void;
}

export class TextField extends Component {
	static override styles = [styles()];

	static override get properties() {
		return {
			clearable: { type: Boolean },
			disabled: { type: Boolean, reflect: true },
			max: { type: String },
			maxLength: { type: Number, reflect: true, attribute: 'max-length' },
			min: { type: String },
			minLength: { type: Number, reflect: true, attribute: 'min-length' },
			pattern: { type: String },
			placeholder: { type: String },
			readOnly: { type: Boolean, reflect: true, attribute: 'read-only' },
			size: { type: String },
			status: { type: String },
			step: { type: String },
			type: { type: String },
			value: { type: String },
		};
	}

	/**
	 * Should the text field be clearable.
	 */
	declare clearable: TextFieldProps['clearable'];

	/**
	 * Should the text field be disabled.
	 */
	declare disabled: TextFieldProps['disabled'];

	/**
	 * Define the greatest value in the range of permitted values.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#max
	 */
	declare max: TextFieldProps['max'];

	/**
	 * The maximum number of characters a user can enter into the text field. Set
	 * to -1 for none.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#maxlength
	 */
	declare maxLength: TextFieldProps['max-length'];

	/**
	 * Define the most negative value in the range of permitted values.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#min
	 */
	declare min: TextFieldProps['min'];

	/**
	 * The minimum number of characters a user can enter into the text field. Set
	 * to -1 for none.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#minlength
	 */
	declare minLength: TextFieldProps['min-length'];

	/**
	 * A regular expression that the text field's value must match to pass
	 * constraint validation.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#pattern
	 */
	declare pattern: TextFieldProps['pattern'];

	/**
	 * Placeholder text for the input.
	 */
	declare placeholder: TextFieldProps['placeholder'];

	/**
	 * Indicate whether or not a user should be able to edit the text field's
	 * value.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#readonly
	 */
	declare readOnly: TextFieldProps['read-only'];

	/**
	 * Return or set the element's step attribute, which works with min and max
	 * to limit the increments at which a numeric or date-time value can be set.
	 *
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#step
	 */
	declare step: TextFieldProps['step'];

	/**
	 * Text field size.
	 */
	declare size: TextFieldProps['size'];

	/**
	 * Input type.
	 */
	declare type: TextFieldProps['type'];

	/**
	 * Text field value.
	 */
	declare value?: TextFieldProps['value'];

	/**
	 * Text field status type.
	 */
	declare status: TextFieldProps['status'];

	private inputElement: HTMLInputElement | null = null;

	/**
	 * If there's a m-field ancestor, we want to consume its context.
	 */
	private field = new ContextConsumer(this, fieldContext, undefined, true);

	constructor() {
		super();

		this.clearable = false;
		this.disabled = false;
		this.placeholder = undefined;
		this.size = 'default';
		this.type = 'text';
		this.value = undefined;
		this.max = '';
	}

	override connectedCallback() {
		super.connectedCallback();

		this.setAttribute('role', 'textbox');
	}

	override render() {
		return html`
			<div
				class=${classMap({
					container: true,

					'-size-small': this.size === 'small',
					'-size-default': this.size === 'default',
					'-size-large': this.size === 'large',

					'is-disabled': Boolean(this.disabled),

					'-status-default': this.#getStatus() === 'default',
					'-status-danger': this.#getStatus() === 'danger',
				})}
			>
				<slot name="leading"></slot>
				<input
					id="${ifDefined(this.id)}"
					type="${ifDefined(this.type)}"
					class="text-field"
					aria-disabled=${this.disabled ? 'true' : 'false'}
					?disabled="${this.disabled}"
					?readonly=${this.readOnly}
					max=${(this.max || nothing) as unknown as number}
					maxlength=${this.maxLength != null && this.maxLength > -1
						? this.maxLength
						: nothing}
					min=${(this.min || nothing) as unknown as number}
					minlength=${this.minLength != null && this.minLength > -1
						? this.minLength
						: nothing}
					pattern=${ifDefined(this.pattern)}
					placeholder="${ifDefined(this.placeholder)}"
					step=${(this.step || nothing) as unknown as number}
					value="${ifDefined(this.value)}"
					@change=${this.reemit}
					@input=${this.#handleInput}
				/>
				${this.clearable
					? html`<m-close-button
							class=${classMap({
								clear: true,

								/**
								 * we use this approach to avoid the layout shift
								 * when showing/hiding the clear button as the users
								 * changes the input value.
								 */
								'is-visible': this.#isClearVisible(),
							})}
							controls="${ifDefined(this.id)}"
							@m-close=${this.#handleClear}
							label="Clear input"
					  ></m-close-button>`
					: nothing}
				<slot name="trailing"></slot>
			</div>
		`;
	}

	get input(): HTMLInputElement | null {
		if (this.inputElement == null) {
			this.inputElement = this.shadowRoot?.querySelector('input') ?? null;
		}

		return this.inputElement;
	}

	clear() {
		this.value = '';

		if (this.input) {
			this.input.value = '';
		}

		this.emit('m-clear');

		this.focus();
	}

	override focus() {
		this.input?.focus();
	}

	#isClearVisible(): boolean {
		const value = this.value || '';

		return !this.disabled && value.length > 0;
	}

	#getStatus() {
		return this.status || this.field.value?.status;
	}

	#handleInput(event: Event) {
		this.value = (event.target as HTMLInputElement).value;

		this.reemit(event);
	}

	#handleClear(event: Event) {
		event.stopPropagation();

		this.clear();
	}
}

window.customElements.define('m-text-field', TextField);

declare global {
	interface HTMLElementTagNameMap {
		'm-text-field': TextField;
	}
}
